import * as React from "react";
import { Minus } from "lucide-react";
import { OTPInput, OTPInputContext } from "input-otp";
import { cn } from "@/lib/utils";

const InputOTP = React.forwardRef<HTMLInputElement, React.ComponentProps<typeof OTPInput>>(
  ({ className, containerClassName, ...props }, ref) => (
    <OTPInput
      ref={ref}
      data-slot="input-otp"
      containerClassName={cn(
        "group flex items-center has-disabled:cursor-not-allowed has-disabled:opacity-50",
        containerClassName,
      )}
      className={cn("disabled:cursor-not-allowed", className)}
      {...props}
    />
  ),
);
InputOTP.displayName = "InputOTP";

function InputOTPGroup({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="input-otp-group" className={cn("flex items-center gap-2", className)} {...props} />;
}

function InputOTPSlot({ index, className, ...props }: React.ComponentProps<"div"> & { index: number }) {
  const inputOTPContext = React.useContext(OTPInputContext);
  const { char, hasFakeCaret, isActive } = inputOTPContext.slots[index];

  return (
    <div
      data-slot="input-otp-slot"
      data-active={isActive}
      data-filled={Boolean(char)}
      aria-hidden="true"
      className={cn(
        "relative flex h-14 min-w-0 flex-1 items-center justify-center rounded-md border border-control bg-canvas font-mono text-2xl tabular-nums text-ink transition-colors",
        "group-has-disabled:border-line group-has-disabled:bg-panel-raised group-has-disabled:text-muted",
        char && "border-muted bg-panel-raised",
        "aria-invalid:border-danger aria-invalid:ring-1 aria-invalid:ring-danger/20",
        isActive &&
          "border-focus ring-2 ring-focus aria-invalid:border-danger aria-invalid:ring-2 aria-invalid:ring-danger",
        className,
      )}
      {...props}
    >
      {char}
      {hasFakeCaret && (
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center" aria-hidden="true">
          <span className="h-6 w-px animate-pulse bg-ink" />
        </span>
      )}
    </div>
  );
}

function InputOTPSeparator(props: React.ComponentProps<"div">) {
  return (
    <div data-slot="input-otp-separator" role="separator" {...props}>
      <Minus className="size-4 text-muted" aria-hidden="true" />
    </div>
  );
}

export { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot };
